import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import useSeo from "@/lib/useSeo";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function AccountDashboard() {
  const { user, logout, wishlist } = useAuth();
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [wishItems, setWishItems] = useState([]);
  const [addr, setAddr] = useState({ first_name: "", last_name: "", street: "", house_number: "", postal_code: "", city: "", country: "Nederland" });
  useSeo({ title: "Mijn account — ONUSA" });

  useEffect(() => {
    api.get("/account/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/account/addresses").then(({ data }) => setAddresses(data)).catch(() => {});
    api.get("/account/wishlist").then(({ data }) => setWishItems(data)).catch(() => {});
  }, [wishlist]);

  const saveAddr = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/account/addresses", addr);
      setAddresses(data.addresses);
      setAddr({ first_name: "", last_name: "", street: "", house_number: "", postal_code: "", city: "", country: "Nederland" });
      toast.success("Adres opgeslagen");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delAddr = async (id) => { const { data } = await api.delete(`/account/addresses/${id}`); setAddresses(data.addresses); };
  const exportData = async () => { const { data } = await api.get("/account/export"); const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }); const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = "onusa-mijn-gegevens.json"; a.click(); };
  const deleteAccount = async () => { if (!window.confirm("Je account en gegevens verwijderen? Dit kan niet ongedaan worden gemaakt.")) return; await api.delete("/account"); await logout(); toast.success("Account verwijderd"); };

  const STATUS_NL = { paid: "BETAALD", pending: "IN AFWACHTING", open: "OPEN", failed: "MISLUKT", cancelled: "GEANNULEERD", canceled: "GEANNULEERD", refunded: "TERUGBETAALD", unfulfilled: "NIET VERWERKT", processing: "IN BEHANDELING", shipped: "VERZONDEN", delivered: "BEZORGD", manual: "HANDMATIG", failed_sync: "SYNC MISLUKT" };
  const badge = (s) => <span className="label-x px-2 py-0.5 border border-[#E8E1D8]">{STATUS_NL[s] || (s || "").toUpperCase()}</span>;

  return (
    <div className="onusa-container py-12 md:py-16" data-testid="account-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="label-x">ONUSA ACCOUNT</p>
          <h1 className="heading-x text-3xl mt-2">{user?.name || user?.email}</h1>
        </div>
        <button onClick={logout} className="label-x" data-testid="logout-btn">UITLOGGEN</button>
      </div>
      <Tabs defaultValue="orders">
        <TabsList className="bg-transparent gap-2 mb-6 flex-wrap h-auto">
          {[["orders", "BESTELLINGEN"], ["wishlist", "VERLANGLIJST"], ["addresses", "ADRESSEN"], ["privacy", "PRIVACY"]].map(([t, lbl]) => (
            <TabsTrigger key={t} value={t} className="label-x data-[state=active]:bg-[#1D1D1B] data-[state=active]:text-[#F7F5F1] px-4 py-2" data-testid={`account-tab-${t}`}>{lbl}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="orders">
          {orders.length === 0 ? <p className="text-sm text-[#77736D]">Nog geen bestellingen. <Link to="/shop" className="underline">Begin met shoppen</Link>.</p> : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.number} className="border border-[#E8E1D8] bg-white p-5" data-testid={`account-order-${o.number}`}>
                  <div className="flex flex-wrap justify-between gap-3 items-center">
                    <div><p className="heading-x text-sm">{o.number}</p><p className="label-x mt-1">{new Date(o.created_at).toLocaleDateString("nl-NL")}</p></div>
                    <div className="flex gap-2 items-center">{badge(o.payment_status)}{badge(o.fulfilment_status)}<span className="font-mono text-sm">€{o.total.toFixed(2)}</span></div>
                  </div>
                  {o.tracking?.number && <p className="label-x mt-3">TRACK & TRACE: {o.tracking.carrier} {o.tracking.number} {o.tracking.url && <a href={o.tracking.url} className="underline" target="_blank" rel="noreferrer">VOLGEN</a>}</p>}
                  <div className="flex gap-3 mt-3 flex-wrap">
                    {o.items.map((it, i) => <span key={i} className="text-xs text-[#77736D]">{it.name} {it.size ? `(${it.size})` : ""} ×{it.quantity}</span>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="wishlist">
          {wishItems.length === 0 ? <p className="text-sm text-[#77736D]">Je verlanglijst is leeg.</p> : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">{wishItems.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          )}
        </TabsContent>

        <TabsContent value="addresses">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-3">
              {addresses.map((a) => (
                <div key={a.id} className="border border-[#E8E1D8] bg-white p-4 flex justify-between">
                  <div className="text-sm"><p className="font-medium">{a.first_name} {a.last_name}</p><p className="text-[#77736D]">{a.street} {a.house_number}, {a.postal_code} {a.city}, {a.country}</p></div>
                  <button onClick={() => delAddr(a.id)} className="label-x">VERWIJDEREN</button>
                </div>
              ))}
              {addresses.length === 0 && <p className="text-sm text-[#77736D]">Geen opgeslagen adressen.</p>}
            </div>
            <form onSubmit={saveAddr} className="space-y-3" data-testid="address-form">
              <p className="label-x">ADRES TOEVOEGEN</p>
              <div className="grid grid-cols-2 gap-3">
                {[["first_name", "Voornaam"], ["last_name", "Achternaam"], ["street", "Straat"], ["house_number", "Huisnr."], ["postal_code", "Postcode"], ["city", "Plaats"], ["country", "Land"]].map(([k, ph]) => (
                  <input key={k} required={k !== "country"} placeholder={ph} value={addr[k]} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} className="border border-[#E8E1D8] px-3 py-2 text-sm bg-white" data-testid={`addr-${k}`} />
                ))}
              </div>
              <button className="btn-x-outline" data-testid="save-address-btn">ADRES OPSLAAN</button>
            </form>
          </div>
        </TabsContent>

        <TabsContent value="privacy">
          <div className="max-w-lg space-y-6">
            <div><p className="label-x mb-2">GEGEVENS EXPORTEREN (AVG)</p><p className="text-sm text-[#77736D] mb-3">Download alle persoonsgegevens die we van je bewaren.</p><button onClick={exportData} className="btn-x-outline" data-testid="export-data-btn">EXPORTEER MIJN GEGEVENS</button></div>
            <div className="rule-x pt-6"><p className="label-x mb-2">ACCOUNT VERWIJDEREN</p><p className="text-sm text-[#77736D] mb-3">Verwijder je account en persoonsgegevens permanent.</p><button onClick={deleteAccount} className="label-x text-[#9B1C1C] border border-[#9B1C1C] px-5 py-2.5" data-testid="delete-account-btn">ACCOUNT VERWIJDEREN</button></div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
